/**
 * web/src/lib/senderName.ts
 * Sender-name substitution for the email-gate preview. Mirrors the backend
 * (src/pixel/workflows/sponsor/email_step.py `first_name` + the step_06/step_08
 * `[Your Name]` replace) so the dashboard preview matches the email that
 * actually sends: the sender voice is FIRST-name only ("My name is Sydney" /
 * "Best, Sydney"), and the `[Your Name]` sentinel can occur more than once
 * (intro + sign-off), so EVERY occurrence is replaced — not just the first.
 */

/** The literal sentinel the server leaves in the body for late substitution. */
export const SENDER_NAME_SENTINEL = '[Your Name]';

// Case-insensitive: the speaker workflow briefly shipped a lowercase
// "[Your name]" variant (2026-07-21) — gates drafted then still carry it.
const SENDER_NAME_SENTINEL_RE = /\[your name\]/gi;

/**
 * Best-effort first name: trim, split on whitespace, take the first token.
 * Returns '' for null/empty. Matches backend `first_name`.
 */
export function firstNameFromFull(fullName: string | null | undefined): string {
  return fullName?.trim().split(/\s+/)[0] ?? '';
}

/**
 * Replace every `[Your Name]` sentinel in an email body with the approver's
 * FIRST name. Returns the body unchanged when no first name is available (so
 * the sentinel survives for the server's own onApproval substitution).
 *
 * @param body the rendered email body (HTML) from the gate payload
 * @param fullName the approver's full display name (logged-in user for preview)
 * @returns the body with all sentinels filled, or unchanged if no name
 * @sideEffects none
 * @idempotent yes — re-running on an already-substituted body is a no-op
 */
export function substituteSenderName(body: string, fullName: string | null | undefined): string {
  const first = firstNameFromFull(fullName);
  return first ? body.replace(SENDER_NAME_SENTINEL_RE, first) : body;
}
